import { useEffect, useState } from 'react'
import {
  getTimeLeftUntil,
  hasCountdownElapsed,
  TimeLeft,
  ZERO_TIME_LEFT,
} from '@sportninja/common/features/registration/countdown'

type UseRegistrationCountdownResult = {
  timeLeft: TimeLeft
  hasElapsed: boolean
}

/**
 * Ticks the "registration will be open in" countdown and reloads the page once
 * it reaches zero.
 *
 * A reload (rather than local state) is deliberate: the open/closed gate comes
 * from `useRegistrationPeriod`, which recomputes only on render and has no
 * timer of its own, and the page still needs to create a registration session
 * on mount. Re-running the whole load is what actually moves the visitor into
 * the live registration flow, so they no longer have to refresh by hand.
 */
export const useRegistrationCountdown = (
  startsAt: string | null | undefined,
  onElapsed: () => void = () => window.location.reload()
): UseRegistrationCountdownResult => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    getTimeLeftUntil(startsAt, Date.now())
  )
  const [hasElapsed, setHasElapsed] = useState<boolean>(() =>
    hasCountdownElapsed(startsAt, Date.now())
  )

  useEffect(() => {
    // Already open by the time we mounted — reload immediately rather than
    // showing a countdown that is finished before it starts.
    if (hasCountdownElapsed(startsAt, Date.now())) {
      setTimeLeft(ZERO_TIME_LEFT)
      setHasElapsed(true)
      onElapsed()
      return
    }

    setHasElapsed(false)

    const tick = () => {
      const now = Date.now()
      setTimeLeft(getTimeLeftUntil(startsAt, now))

      if (hasCountdownElapsed(startsAt, now)) {
        // Stop ticking before reloading so a slow unload can't queue another
        // reload on top of this one.
        clearInterval(timer)
        setHasElapsed(true)
        onElapsed()
      }
    }

    tick()
    const timer = setInterval(tick, 1000)

    return () => clearInterval(timer)
    // `onElapsed` is intentionally omitted: callers commonly pass an inline
    // function, and depending on it would tear down and rebuild the interval
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startsAt])

  return { timeLeft, hasElapsed }
}
